import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiShoppingCart, FiHeart, FiUser, FiX, FiPlus, FiMinus } from 'react-icons/fi';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { useApp } from '../../context/AppContext';
import './NavBar.css';

// Single source of truth: the Browse menu comes from the categories table via
// GET /api/categories (`menu` = gender -> [sub-categories]), the same table the
// CRM portal edits. Each gender's list already ends with its "More" bucket.

const USER_MENU = [
  { label: 'My Profile', path: '/account?tab=profile' },
  { label: 'Current Orders', path: '/cart?tab=current' },
  { label: 'Previous Orders', path: '/cart?tab=previous' },
  { label: 'Refunds', path: '/cart?tab=refunds' },
  { label: 'Wishlist', path: '/cart?tab=wishlist' },
  { label: 'Cart', path: '/cart?tab=cart' },
];

export default function NavBar() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { cartCount, wishlistCount } = useApp();
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuData, setMenuData] = useState({});
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);
  const hamburgerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    axios.get('/api/categories')
      .then((res) => { if (!cancelled) setMenuData(res.data.menu || {}); })
      .catch(() => { if (!cancelled) setMenuData({}); });
    return () => { cancelled = true; };
  }, []);
  useEffect(() => {
    const handler = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) setUserMenuOpen(false);
      if (hamburgerRef.current && !hamburgerRef.current.contains(e.target) && !e.target.closest('.side-menu')) setMenuOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleHomeClick = () => {
    if (window.location.pathname === '/') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigate('/');
    }
  };

  const handleSubClick = (category, sub) => {
    navigate(`/catalog?category=${encodeURIComponent(category)}&type=${encodeURIComponent(sub)}`);
    setMenuOpen(false);
  };

  const handleCategoryClick = (cat) => {
    navigate(`/catalog?category=${encodeURIComponent(cat)}`);
    setMenuOpen(false);
  };

  return (
    <>
      <nav className="navbar">
        {/* Hamburger */}
        <div className="nav-left" ref={hamburgerRef}>
          <button className="hamburger" onClick={() => setMenuOpen(o => !o)} aria-label="Menu">
            {menuOpen ? <FiX size={22} /> : <span className="burger-lines"><span/><span/><span/></span>}
          </button>
        </div>

        {/* Logo */}
        <div className="nav-logo" onClick={handleHomeClick}>
          <div className="nav-logo-row">
            <img src="/logo.png" alt="Abhiras" className="nav-logo-img" />
            <div className="logo-text">
              <span className="logo-main">Abhiras</span>
              <span className="logo-sub">Jewellery</span>
            </div>
          </div>
          <span className="logo-caption">Handcrafted Art Jewellery</span>
        </div>

        {/* Right icons */}
        <div className="nav-right">
          <button className="icon-btn" onClick={() => navigate('/cart?tab=wishlist')} title="Wishlist">
            <FiHeart size={20} />
            {wishlistCount > 0 && <span className="badge">{wishlistCount}</span>}
          </button>
          <button className="icon-btn" onClick={() => navigate('/cart?tab=cart')} title="Cart">
            <FiShoppingCart size={20} />
            {cartCount > 0 && <span className="badge">{cartCount}</span>}
          </button>
          <div className="user-menu-wrap" ref={userMenuRef}>
            <button className="icon-btn" onClick={() => setUserMenuOpen(o => !o)} title="Account">
              <FiUser size={20} />
            </button>
            {userMenuOpen && (
              <div className="user-dropdown">
                {user ? (
                  <>
                    <div className="user-greeting">Hi, {user.name?.split(' ')[0] || user.username}</div>
                    {USER_MENU.map(item => (
                      <button key={item.label} onClick={() => { navigate(item.path); setUserMenuOpen(false); }}>
                        {item.label}
                      </button>
                    ))}
                    <button className="logout-btn" onClick={() => { logout(); setUserMenuOpen(false); }}>Logout</button>
                  </>
                ) : (
                  <>
                    <button onClick={() => { navigate('/auth'); setUserMenuOpen(false); }}>Sign In</button>
                    <button onClick={() => { navigate('/auth?mode=signup'); setUserMenuOpen(false); }}>Sign Up</button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* Side Menu */}
      {menuOpen && <div className="overlay" onClick={() => setMenuOpen(false)} />}
      <div className={`side-menu${menuOpen ? ' open' : ''}`}>
        <div className="side-menu-header">
          <span>Browse</span>
          <button onClick={() => setMenuOpen(false)}><FiX size={20}/></button>
        </div>
        {Object.entries(menuData).map(([cat, subs]) => (
          <div key={cat} className="menu-category">
            <div className="menu-cat-row">
              <button className="menu-cat-btn" onClick={() => handleCategoryClick(cat)}>
                {cat}
              </button>
              <button className="expand-btn" onClick={() => setExpandedCategory(expandedCategory === cat ? null : cat)}>
                {expandedCategory === cat ? <FiMinus size={14}/> : <FiPlus size={14}/>}
              </button>
            </div>
            {expandedCategory === cat && (
              <div className="sub-menu">
                {subs.map(sub => (
                  <button key={sub} className="sub-btn" onClick={() => handleSubClick(cat, sub)}>{sub}</button>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </>
  );
}
